import { ChevronRightIcon, MailIcon } from "@heroicons/react/solid";
import axios from "axios";
import { useEffect, useState } from "react";
import { FaSpinner, FaUserCircle, FaUsers } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import stringToColor from "string-to-color";
import api from "../api";
import { ITeamMembership } from "../api/services/team.service";
import { IUserResponse } from "../api/services/user.service";
import { usePagination } from "../hooks/usePagination";
import { getTags } from "./UserProfileSection";
import Pagination from "./shared/Pagination";
import StatusBadge from "./shared/StatusBadge";

export default function UserListSection({
  teamId,
  pageSize,
}: {
  teamId?: string;
  pageSize?: number;
}) {
  let navigate = useNavigate();

  const [users, setUsers] = useState<IUserResponse[]>([]);
  const [teamMembers, setTeamMembers] = useState<ITeamMembership[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { page, paginationProps, searchTerm, updatePagination, setPage } =
    usePagination();

  useEffect(() => {
    searchUsers(searchTerm, page);
  }, [searchTerm, page]);

  async function searchUsers(searchTerm: string | null, page: number | null) {
    try {
      setLoading(true);
      if (teamId) {
        const { data } = await api.team.getTeamMembers(teamId);
        if (data.result) {
          setTeamMembers(data.result);
          console.log(data.result);
          setLoading(false);
        }
      } else {
        const { data } = await api.user.searchUsers({
          term: searchTerm,
          page,
          size: pageSize || 10,
        });
        if (data.result) {
          const userPageContent = data.result.content;
          setUsers(userPageContent);
          updatePagination(data);
          setLoading(false);
        }
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  function UserRow({
    user,
  }: {
    user: {
      id: string;
      firstname: string;
      lastname: string;
      email?: string;
      onboarded?: boolean;
      verified?: boolean;
      deactivated?: boolean;
    };
  }) {
    return (
      <div
        className="break-all py-4 px-4 cursor-pointer"
        onClick={() => navigate(`/user/${user.id}`)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-3 break-all">
              <FaUserCircle
                className="bg-white rounded-full h-10 w-10"
                color={stringToColor(user.id)}
              />
            </div>

            <div className="break-all">
              <p className="text-sm font-medium text-red-600 truncate">
                {user.firstname} {user.lastname}
              </p>
              {user.email && (
                <p className="mt-2 flex items-start text-sm text-gray-500 break-all">
                  <MailIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  {user.email}
                </p>
              )}
            </div>
          </div>

          <div className="flex">
            <div className="hidden md:flex mr-10">
              {getTags(user.onboarded, user.verified, user.deactivated)}
            </div>
            <ChevronRightIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    );
  }

  function TeamMemberRow({ membership }: { membership: ITeamMembership }) {
    return (
      <div
        className="break-all py-4 px-4 cursor-pointer"
        onClick={() => navigate(`/user/${membership.userId}`)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 break-all">
              <FaUserCircle
                className="bg-white rounded-full h-10 w-10"
                color={stringToColor(membership.userId)}
              />
            </div>

            <div className="break-all">
              <p className="text-sm font-medium text-red-600 truncate">
                {membership.user.firstname} {membership.user.lastname}
              </p>
              {membership.user.email && (
                <p className="mt-2 flex items-start text-sm text-gray-500 break-all">
                  <MailIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  {membership.user.email}
                </p>
              )}
            </div>
          </div>

          <div className="flex space-x-4">
            <StatusBadge
              status={membership.role}
              className={
                membership.role === "OWNER" ? "bg-red-200 text-red-800" : ""
              }
            />
            {/* <div
              className="text-indigo-600 mr-2 hover:text-orange-600 rounded-xl transition-colors ease-in-out duration-200 cursor-pointer"
              onClick={(event) => {
                event.stopPropagation();
                // removeTeamMember(membership.teamId, membership.userId);
              }}
            >
              <FaTrash size={20} />
            </div> */}

            <ChevronRightIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow">
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10 rounded-lg">
            <div className="">
              <FaUsers size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex ">
              {!teamId ? "Users" : "Members"}
            </span>
          </div>
        </div>
        <div className="font-semibold text-slate-700">
          {paginationProps.totalElements || users.length}
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && users.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-200 w-full overflow-scroll overflow-x-hidden ">
          {users.map((user, i) => (
            <UserRow key={i} user={user} />
          ))}
        </div>
      )}
      {!loading && teamMembers.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-200 w-full overflow-scroll overflow-x-hidden ">
          {teamMembers.map((membership, i) => (
            <TeamMemberRow key={i} membership={membership} />
          ))}
        </div>
      )}
      {!loading && users.length === 0 && teamMembers.length === 0 && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            No Users found
          </div>
          <div className="inline-flex items-center space-x-2">
            <div>Maybe try different search parameters</div>
          </div>
        </div>
      )}
      <Pagination {...paginationProps} goToPage={setPage} />
    </div>
  );
}
